import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { FormEvent, ReactElement, useEffect, useState } from "react";
import InputMask from "react-input-mask";
import { IoMdArrowBack } from "react-icons/io";
import VerificationInput from "react-verification-input";
import { useHistory } from "react-router-dom";
import { IoShieldCheckmarkOutline } from "react-icons/io5";
import {
  TbDeviceMobileCheck,
  TbDeviceMobileExclamation,
  TbDeviceMobileMessage,
} from "react-icons/tb";
import useConfirmationCode from "../../hooks/useConfirmationCode";
import { ConfirmationCodeMethod } from "../../@types/ConfirmationCode";
import { useUser } from "../../hooks/useUser";

type Props = {
  show: boolean;
  onClickBack: CallableFunction;
  email: string;
  username: string;
  password: string;
  confirmPassword: string;
};
enum eStep {
  FIRST_STEP,
  SECOND_STEP,
  SUCCESS,
}
export default function PhoneConfirmationModal({
  onClickBack: backToSignupPage,
  username,
  email,
  password,
}: Props) {
  const [step, setStep] = useState<eStep>(eStep.FIRST_STEP);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [phoneValue, setPhoneValue] = useState<string>("");
  const [method, setMethod] = useState<ConfirmationCodeMethod>("sms");
  const [codeValue, setCodeValue] = useState<string>("");
  const [canConfirm, setCanConfirm] = useState<boolean>(false);

  const [firstStepErrorMsg, setFirstStepErrorMsg] = useState<string>("");
  const [secondStepErrorMsg, setSecondStepErrorMsg] = useState<string>("");

  const { createPhoneCode } = useConfirmationCode();
  const { createAccount } = useUser();
  const { push } = useHistory();

  useEffect(() => {
    setCanConfirm(codeValue.length === 5);
  }, [codeValue]);

  async function handleFirstStepSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (isLoading) {
      return;
    }

    setFirstStepErrorMsg("");
    setIsLoading(true);

    const responseMessage = await createPhoneCode(phoneValue, method);

    switch (responseMessage) {
      case "This phone number is already taken by another user.":
        setFirstStepErrorMsg(
          "Este número de telefone não está disponível para uso."
        );
        break;
      case "You can only request a code every 2 minutes":
        setFirstStepErrorMsg(
          "Você só pode solicitar um novo código a cada 2 minutos."
        );
        break;
      case "Code sent successfully":
        setStep(eStep.SECOND_STEP);
        break;
      default:
        setFirstStepErrorMsg(
          "Ocorreu um erro desconhecido ao enviar o código, por favor, tente novamente."
        );
        break;
    }

    setIsLoading(false);
  }

  async function handleSecondStepSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (isLoading) {
      return;
    }
    setSecondStepErrorMsg("");
    setIsLoading(true);

    const requestNewAccount = await createAccount({
      name: username,
      email: email,
      password,
      phone: phoneValue.replace(/[^0-9]/g, ""),
      confirmation_code: codeValue,
    });

    //Unknown error on request.
    if (!requestNewAccount) {
      setSecondStepErrorMsg(
        "Ocorreu um erro desconhecido, por favor, tente novamente."
      );
      setIsLoading(false);
      return;
    }

    const { status, msg } = requestNewAccount;

    if (status === 1) {
      push("/welcome");
      return;
    } else if (msg === "Error on database") {
      setSecondStepErrorMsg(
        "Não foi possivel criar sua conta, talvez você já tenha um cadastro, por favor, tente realizar login."
      );
    } else if (msg === "Error on fields") {
      setSecondStepErrorMsg(
        "Ocorreu um erro nos campos, por favor, tente novamente."
      );
    } else if (msg === "Invalid confirmation code") {
      setSecondStepErrorMsg(
        "Código de confirmação inválido, por favor, tente novamente."
      );
    } else {
      setSecondStepErrorMsg(
        "Ocorreu um erro desconhecido, por favor, tente novamente."
      );
    }

    setIsLoading(false);
  }

  if (isLoading) {
    return <LoadingState />;
  }

  if (step === eStep.FIRST_STEP) {
    return (
      <Modal size="lg" fullscreen={true} show={true}>
        <div className="container mw-95">
          <Modal.Header>
            <IoMdArrowBack
              onClick={() => backToSignupPage()}
              className="float-left cursor-pointer"
              size={26}
            />
          </Modal.Header>

          <div className="row">
            <div
              className="col-lg-12 shadow-lg d-flex align-items-center rounded-3 rounded-start-0 "
              style={{
                backgroundColor: "var(--bg-1)",
                borderRadius: 10,
              }}
            >
              <div className="container my-auto text-center py-4 pd-l-10 pd-r-10">
                {
                  <TbDeviceMobileExclamation
                    size={40}
                    color="var(--primary)"
                    className="mb-2"
                  />
                }
                <h2 className="text-black">Confirme seu número de telefone.</h2>
                <p className="tx-14">
                  Insira seu número de telefone para receber o código de
                  confirmação.
                </p>

                <div className="row justify-content-center">
                  <div className="col-12 col-lg-8">
                    <Form onSubmit={handleFirstStepSubmit}>
                      <Form.Group className="mb-3 ">
                        <Form.Control
                          value={phoneValue}
                          required
                          onChange={(e) => {
                            if (e.target.value) {
                              const realNumbers = e.target.value.replace(
                                /[^0-9]/g,
                                ""
                              );
                              if (realNumbers.length < 11) {
                                e.target.setCustomValidity(
                                  "Preencha seu número de telefone completamente"
                                );
                              } else {
                                e.target.setCustomValidity("");
                              }
                            }

                            setPhoneValue(e.target.value);
                          }}
                          type="text"
                          className="text-center tx-20"
                          as={InputMask}
                          mask="(99) 99999-9999"
                          placeholder="(99) 99999-9999"
                        />
                      </Form.Group>

                      <Form.Group className="mb-4">
                        <Form.Control
                          as="select"
                          size="lg"
                          value={method}
                          onChange={(e) => {
                            setMethod(e.target.value as ConfirmationCodeMethod);
                          }}
                          className="text-center tx-16"
                        >
                          <option value="sms" defaultChecked>
                            SMS
                          </option>
                          <option value="whatsapp">WhatsApp</option>
                        </Form.Control>
                      </Form.Group>

                      {firstStepErrorMsg.length > 0 && (
                        <div className="centered-flex">
                          <strong className="tx-14 text-danger mb-2">
                            {firstStepErrorMsg}
                          </strong>
                        </div>
                      )}

                      <Button
                        className="rounded-button cursor-pointer"
                        style={{
                          backgroundColor: "var(--primary)",
                        }}
                        type="submit"
                      >
                        Próximo
                      </Button>

                      <p className="tx-12 mt-3">
                        Seu número de telefone não será compartilhado com
                        ninguém e será apenas utilizado para validar seu
                        cadastro.
                      </p>
                    </Form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    );
  } else if (step === eStep.SECOND_STEP) {
    return (
      <Modal size="lg" fullscreen={true} show={true}>
        <div className="container mw-95">
          <Modal.Header>
            <IoMdArrowBack
              onClick={() => setStep(eStep.FIRST_STEP)}
              className="float-left cursor-pointer"
              size={26}
            />
          </Modal.Header>

          <div className="row">
            <div
              className="col-lg-12 shadow-lg d-flex align-items-center rounded-3 rounded-start-0 "
              style={{
                backgroundColor: "var(--bg-1)",
                borderRadius: 10,
              }}
            >
              <div className="container my-auto text-center py-4 pd-l-10 pd-r-10">
                {
                  <TbDeviceMobileMessage
                    size={40}
                    color="var(--primary)"
                    className="mb-2"
                  />
                }
                <h2 className="text-black">
                  Confirme o código que enviamos para seu celular.
                </h2>
                <p className="tx-14">
                  Enviamos um código para o telefone{" "}
                  <strong>{phoneValue}</strong> via{" "}
                  <strong>{methodToName(method)}</strong>, por favor, insira o
                  código abaixo.
                </p>
                <div className="centered-flex">
                  <Form onSubmit={handleSecondStepSubmit} className="mw-100">
                    <Form.Group className="mb-3 d-flex justify-content-center">
                      <VerificationInput
                        validChars="0-9"
                        autoFocus
                        length={5}
                        inputProps={{ inputMode: "numeric" }}
                        onChange={(value) => setCodeValue(value)}
                        value={codeValue}
                      />
                    </Form.Group>

                    {secondStepErrorMsg.length > 0 && (
                      <div className="centered-flex">
                        <strong className="tx-14 text-danger mb-2">
                          {secondStepErrorMsg}
                        </strong>
                      </div>
                    )}

                    <Button
                      className="rounded-button cursor-pointer"
                      variant={"primary"}
                      disabled={!canConfirm}
                      color="var('--text-primary')"
                      type="submit"
                    >
                      Confirmar
                    </Button>
                  </Form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    );
  } else if (step === eStep.SUCCESS) {
    return <DoneModal />;
  }

  return <></>;
}

function DoneModal(): ReactElement {
  const { push } = useHistory();

  return (
    <Modal size="lg" fullscreen={true} show={true}>
      <div className="container mw-95">
        <div className="row">
          <div
            className="col-lg-12 shadow-lg d-flex align-items-center rounded-3 rounded-start-0 "
            style={{
              backgroundColor: "var(--bg-1)",
              borderRadius: 10,
            }}
          >
            <div className="container my-auto text-center py-4 pd-l-10 pd-r-10">
              {<TbDeviceMobileCheck color="var(--success)" size={40} />}
              <h2 className="text-black">Confirmado!</h2>
              <div className="centered-flex">
                <p className="tx-14 text-center">
                  Seu número de telefone foi confirmado com sucesso, você pode
                  clicar no botão abaixo para acessar a plataforma.
                </p>
              </div>

              <div className="centered-flex">
                <Button
                  className="rounded-button cursor-pointer"
                  variant={"primary"}
                  onClick={() => push("/welcome")}
                  type="button"
                >
                  Acessar plataforma.
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}

function LoadingState(): ReactElement {
  return (
    <Modal size="lg" fullscreen={true} show={true}>
      <div className="container mw-95">
        <div className="row">
          <div
            className="col-lg-12 shadow-lg d-flex align-items-center rounded-3 rounded-start-0 "
            style={{
              backgroundColor: "var(--bg-1)",
              borderRadius: 10,
            }}
          >
            <div className="container my-auto text-center py-4 pd-l-10 pd-r-10">
              {<IoShieldCheckmarkOutline color="var(--primary)" size={32} />}
              <h2 className="text-black">Processando com segurança.</h2>
              <strong className="tx-14">
                <Spinner animation="border" size="sm" />
              </strong>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}

function methodToName(method: ConfirmationCodeMethod): string {
  switch (method) {
    case "sms":
      return "SMS";
    case "whatsapp":
      return "WhatsApp";
    default:
      return "";
  }
}
